import { useEffect, useMemo, useState } from "react";
import {
  AutoComplete,
  Button,
  Flex,
  Form,
  Input,
  Mentions,
  Modal,
  Switch,
  Table,
  Tag,
  Typography,
  notification,
} from "antd";
import { EditOutlined } from "@ant-design/icons";
import axiosInstance from "../axios";

const { Paragraph, Text } = Typography;

const applyTemplateString = (template, variables = {}) => {
  if (!template) return "";
  return String(template).replace(/\{(\w+)\}/g, (_, key) => {
    const value = variables[key];
    return value == null ? "" : String(value);
  });
};

const toWatiTemplateName = (value) => {
  if (value == null) return null;
  if (typeof value === "object") {
    value = value.value ?? value.label ?? value.target?.value ?? "";
  }
  const trimmed = String(value).trim();
  return trimmed || null;
};

const WhatsappTemplates = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [watiNames, setWatiNames] = useState([]);
  const [form] = Form.useForm();
  const sessionBody = Form.useWatch("session_body", form);

  const mentionVariables = editingItem?.variables || [];

  const mentionOptions = useMemo(
    () =>
      mentionVariables.map((item) => ({
        key: item.key,
        value: `${item.key}}`,
        label: (
          <div>
            <Text strong>{`{${item.key}}`}</Text>
            <div style={{ fontSize: 12, color: "#666" }}>{item.description}</div>
          </div>
        ),
      })),
    [mentionVariables],
  );

  const sampleValues = useMemo(
    () =>
      mentionVariables.reduce((acc, item) => {
        acc[item.key] = item.sample || "";
        return acc;
      }, {}),
    [mentionVariables],
  );

  const previewText = applyTemplateString(sessionBody, sampleValues);

  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await axiosInstance.get("/v1/cre/whatsapp-templates");
      setData(response.data?.data || []);
    } catch (error) {
      notification.error({
        message: "Failed to load templates",
        description: error.response?.data?.message || "Could not fetch WhatsApp templates.",
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchWatiNames = async () => {
    try {
      const response = await axiosInstance.get("/v1/cre/wati/templates");
      const templates = response.data?.data?.messageTemplates || response.data?.data?.templates || [];
      const names = templates
        .map((item) => item.elementName || item.name || item.templateName)
        .filter(Boolean);
      setWatiNames([...new Set(names)].sort());
    } catch (_error) {
      setWatiNames([]);
    }
  };

  useEffect(() => {
    fetchData();
    fetchWatiNames();
  }, []);

  const handleEdit = (record) => {
    setEditingItem(record);
    setModalOpen(true);
  };

  useEffect(() => {
    if (!modalOpen || !editingItem) return;
    form.setFieldsValue({
      display_name: editingItem.display_name,
      session_body: editingItem.session_body || "",
      wati_template_name: editingItem.wati_template_name || "",
      is_active: Boolean(editingItem.is_active),
    });
  }, [modalOpen, editingItem, form]);

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setSubmitLoading(true);
      const response = await axiosInstance.put(`/v1/cre/whatsapp-templates/${editingItem.id}`, {
        display_name: values.display_name,
        session_body: values.session_body,
        wati_template_name: toWatiTemplateName(values.wati_template_name),
        is_active: values.is_active,
      });
      if (response.data?.success) {
        notification.success({ message: response.data.message || "Template updated" });
        const updated = response.data.data;
        if (updated?.id) {
          setData((prev) => prev.map((row) => (row.id === updated.id ? { ...row, ...updated } : row)));
        } else {
          fetchData();
        }
        setModalOpen(false);
      } else {
        notification.error({
          message: "Update failed",
          description: response.data?.message || "Unable to update template",
        });
      }
    } catch (error) {
      if (error.errorFields) return;
      notification.error({
        message: "Save failed",
        description: error.response?.data?.message || "Unable to save template",
      });
    } finally {
      setSubmitLoading(false);
    }
  };

  const columns = [
    {
      title: "Key",
      dataIndex: "template_key",
      key: "template_key",
      width: 260,
      render: (key) => <Text code>{key}</Text>,
    },
    {
      title: "Name",
      dataIndex: "display_name",
      key: "display_name",
    },
    {
      title: "Category",
      dataIndex: "category",
      key: "category",
      width: 140,
      render: (category) => <Tag>{category}</Tag>,
    },
    {
      title: "WATI template name",
      key: "wati_template_name",
      render: (_, record) =>
        record.uses_env_template_name ? (
          <Text type="secondary">Using env ({record.env_template_name || "code fallback"})</Text>
        ) : (
          <Text>{record.wati_template_name}</Text>
        ),
    },
    {
      title: "Active",
      dataIndex: "is_active",
      key: "is_active",
      width: 90,
      render: (active) => (active ? <Tag color="green">Yes</Tag> : <Tag>No</Tag>),
    },
    {
      title: "Action",
      key: "action",
      width: 100,
      render: (_, record) => (
        <Button type="primary" size="small" icon={<EditOutlined />} onClick={() => handleEdit(record)}>
          Edit
        </Button>
      ),
    },
  ];

  return (
    <Flex vertical gap={12} style={{ overflow: "scroll", marginLeft: "5px", width: "100%" }}>
      <div>
        <Text strong style={{ fontSize: 16 }}>
          WhatsApp Templates
        </Text>
        <Paragraph type="secondary" style={{ marginBottom: 0, maxWidth: 820 }}>
          Session message body is used when the user has an active 24-hour WhatsApp session. The WATI
          template name is the approved fallback when there is no session. Leave the name empty to keep
          the current environment mapping. Type {"{"} to insert variables.
        </Paragraph>
      </div>

      <Table
        columns={columns}
        dataSource={data}
        rowKey="id"
        loading={loading}
        pagination={false}
        scroll={{ x: 1100 }}
      />

      <Modal
        title={editingItem ? `Edit ${editingItem.display_name}` : "Edit WhatsApp Template"}
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        onOk={handleSubmit}
        confirmLoading={submitLoading}
        okText="Update"
        width={860}
        destroyOnClose
      >
        {editingItem && (
          <Form form={form} layout="vertical">
            <Form.Item label="Template key">
              <Input value={editingItem.template_key} disabled />
            </Form.Item>

            <Form.Item
              name="display_name"
              label="Display name"
              rules={[{ required: true, message: "Display name is required" }]}
            >
              <Input />
            </Form.Item>

            <Form.Item
              name="session_body"
              label="Session message body"
              extra='Used inside the 24-hour session window. Type "{" to insert variables.'
              rules={[{ required: true, message: "Session body is required" }]}
            >
              <Mentions
                prefix="{"
                rows={10}
                placeholder='Type "{" for variables'
                options={mentionOptions}
                filterOption={(input, option) =>
                  String(option?.value || "")
                    .toLowerCase()
                    .includes(String(input || "").toLowerCase())
                }
              />
            </Form.Item>

            {mentionVariables.length > 0 && (
              <Flex gap={8} wrap="wrap" style={{ marginBottom: 16 }}>
                {mentionVariables.map((item) => (
                  <Tag key={item.key} title={item.description}>
                    {`{${item.key}}`}
                  </Tag>
                ))}
              </Flex>
            )}

            {editingItem.wati_parameters?.length > 0 && (
              <Paragraph type="secondary" style={{ marginTop: 0 }}>
                Approved WATI parameters (not editable):{" "}
                {editingItem.wati_parameters.map((name) => (
                  <Tag key={name}>{name}</Tag>
                ))}
              </Paragraph>
            )}

            <Form.Item
              name="wati_template_name"
              label="Approved WATI template name"
              extra={`Leave empty to keep using env/code (${editingItem.env_template_name || "fallback"}). The name must already be approved in WATI.`}
              getValueFromEvent={(value) => {
                if (value == null) return "";
                if (typeof value === "object") {
                  return value.value ?? value.label ?? value.target?.value ?? "";
                }
                return value;
              }}
              normalize={(value) => (value == null ? "" : String(value))}
            >
              <AutoComplete
                options={watiNames.map((name) => ({ value: name }))}
                filterOption={(input, option) =>
                  String(option?.value || "")
                    .toLowerCase()
                    .includes(String(input || "").toLowerCase())
                }
              >
                <Input allowClear placeholder="e.g. prod_prospect_parent_1" />
              </AutoComplete>
            </Form.Item>

            <Form.Item name="is_active" label="Active" valuePropName="checked">
              <Switch />
            </Form.Item>

            <div>
              <Text strong>Preview (sample values)</Text>
              <Paragraph
                style={{
                  whiteSpace: "pre-wrap",
                  background: "#f5f5f5",
                  padding: 12,
                  marginTop: 8,
                  borderRadius: 6,
                }}
              >
                {previewText || "—"}
              </Paragraph>
            </div>
          </Form>
        )}
      </Modal>
    </Flex>
  );
};

export default WhatsappTemplates;
